
import { useState } from 'react';
import { UseFormRegister, UseFormSetValue, UseFormGetValues } from 'react-hook-form';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import type { FormData } from './types';

interface VerifiedCertificationsProps {
  register: UseFormRegister<FormData>;
  setValue: UseFormSetValue<FormData>;
  getValues: UseFormGetValues<FormData>;
}

export const VerifiedCertifications = ({ register, setValue, getValues }: VerifiedCertificationsProps) => {
  // Add local state to track switch states for immediate visual feedback
  const [switchStates, setSwitchStates] = useState<Record<string, boolean>>({
    is_esac_accredited: getValues('is_esac_accredited') === true,
    irs_certified: getValues('irs_certified') === true,
    audited: getValues('audited') === true,
    fully_bonded_insured: getValues('fully_bonded_insured') === true,
    napeo_member: getValues('napeo_member') === true,
    bbb_accredited: getValues('bbb_accredited') === true,
    aicpa_soc: getValues('aicpa_soc') === true,
  });
  
  const handleToggleChange = (id: keyof FormData, checked: boolean) => {
    console.log(`Toggling certification ${id} to ${checked}`);
    setValue(id, checked);
    
    // Update local state for immediate visual feedback
    setSwitchStates(prev => ({
      ...prev,
      [id]: checked
    }));
  };
  
  const CertificationSwitch = ({ id, label }: { id: keyof FormData; label: string }) => {
    // Use the local state for visual feedback
    const isChecked = switchStates[id as string] || false;
    console.log(`Rendering certification switch ${id} with checked=${isChecked}`);
    
    return (
      <div className="flex items-center justify-between space-x-2 py-2">
        <Label>{label}</Label>
        <Switch
          checked={isChecked}
          onCheckedChange={(checked) => handleToggleChange(id, checked)}
        />
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Verified Certifications</h3>
      <p className="text-sm text-muted-foreground mb-4">
        These certifications have been verified by the PEO App team.
      </p>
      
      <div className="space-y-1">
        <CertificationSwitch id="is_esac_accredited" label="ESAC Accredited" />
        <CertificationSwitch id="irs_certified" label="IRS Certified" />
        <CertificationSwitch id="audited" label="Audited" />
        <CertificationSwitch id="fully_bonded_insured" label="Fully Bonded & Insured" />
        <CertificationSwitch id="napeo_member" label="NAPEO Member" />
        <CertificationSwitch id="bbb_accredited" label="BBB Accredited" />
        <CertificationSwitch id="aicpa_soc" label="AICPA SOC" />
      </div>
      
      <Separator className="my-4" />
    </div>
  );
};
